import Controller, { OnCatch, OnClick, OnMount, OnSuccess, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent, RequestEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { ModalProps, ModalService, PanelAPI, ThemeService, ThemeTonalRange } from '@philips-internal/oswald-ui-components';
import { LoginAPI } from '@philips-internal/oswald-ui-foundation';
import { Inject } from '@odinjs/odin';

import BaseController from '../BaseController';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import UserContext from '../../utils/user-context';
import { ThemeLogin } from '../../interfaces/theme-login';
import { LoginUtils } from '../../utils/login-utils';

import './index.css';
import { generateUUID, isMobile } from '@philips-internal/oswald-ui-utils';

interface Resp {
  [key: string]: string;
}

interface ErrorMessageProps {
  isExpiredPassword: boolean;
  message: string;
}

interface ConfigurationLogin {
  dsTitleLogin: string;
}

class EventListenerController {
  static listenerShowLoginMessage: EventListener | undefined;
}
@Controller({ component: 'login-panel', fragment: 'login' })
class LoginController extends BaseController {

  @Inject('new-account-panel')
    newAccountPanel!: PanelAPI;

  @Inject('login')
    login!: LoginAPI;

  @OnClick({ target: 'new-account-button' })
  onClickNewAccountButton(): void {
    this.historyService.push(APPLICATION_ROUTES.ACCOUNT_CREATION_MODAL, {});
  }

  @OnMount({ target: 'login-panel' })
  onMountLogin(): void {
    this.addListenerShowLoginMessage();

    if (!UserContext.twsUserParameters.hasAccessToUserRegistration) {
      this.newAccountPanel.setVisible(false);
    }

    setTimeout(() => {
      const { loginContentTonalRange = ThemeTonalRange.BRIGHT } = getFoundationDataContext().getBulkData();
      ThemeService.setContentTonalRange(loginContentTonalRange as ThemeTonalRange);
    });
  }

  @OnUnmount({ target: 'login-panel' })
  onUnmountLogin(): void {
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
    this.removeListenerShowLoginMessage();
  }

  @OnMount({ target: ['login'] })
  onMount(event: MountEvent): void {
    const api = event.target as unknown as LoginAPI;
    const defaultTitleLogin = MessageService.translate('patient.welcome.landing.page').concat(' ').concat(MessageService.translate('patient.title.landing.page'));

    HttpService.get(APPLICATION_ENDPOINTS.GET_CONFIGURATION)
      .then(response => {
        const { dsLogoLogin } = response.body as ThemeLogin;
        const { dsTitleLogin } = response.body as ConfigurationLogin;

        api.setLogo(dsLogoLogin);
        api.setApplicationName( MessageService.translate(dsTitleLogin || defaultTitleLogin ))
      })
  }

  @OnCatch({ target: ['login'] })
  onCatch(event: RequestEvent<Error | Record<string, string>>): void {
    LoginUtils.handleLoginError(event);
  }

  getErrorMessage(resp: Resp, username: string): ErrorMessageProps {
    const message = Object.keys(resp).reduce((ret, value) => `${ret} ${MessageService.translate(resp[value])}`, '');

    if (Object.keys(resp).find(item => item === '870103') != null) {
      const ms = `${message}. ${MessageService.translate('patient.login.password-expired')}`;
      this.openRenewPasswordModal(username, ms);

      return {
        isExpiredPassword: true,
        message: ms,
      }
    }

    return {
      isExpiredPassword: false,
      message: message,
    };
  }

  openModalConfirmationSmsCode():void{
    const modalProps : ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: true,
      fragment:'account-creation-confirmation-sms-code',
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: MessageService.translate('account.confirmation.modal.title'),
      uuid: generateUUID(),
    };

    if(isMobile()){
      ModalService.openFullscreen(modalProps);
    }else{
      ModalService.openDialog(modalProps);
    }
  }

  openRenewPasswordModal(username: string, message: string): void {
    const modalProps: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      contextData: { message, username },
      focusOnOpen: true,
      fragment: 'change-expired-password',
      onCancel: () => '',
      onSubmit: () => '',
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'patient.change.password',
      uuid: generateUUID(),
    }

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps);
  }

  @OnSuccess('login')
  onSuccess(): void {
    LoginUtils.loginSucess();
  }

  listenerShowLoginMessage = (e: Event): void => {
    const ce: CustomEvent = e as CustomEvent;
    const message = ce.detail as string;
    this.login.setNotification(
      {
        highlight: true,
        level: ToastLevel.SUCCESS,
        message: message,
        title: ' ',
      }
    );
  }

  addListenerShowLoginMessage(): void {
    this.removeListenerShowLoginMessage();
    addEventListener('listenerShowLoginMessage', this.listenerShowLoginMessage.bind(this));
  }

  removeListenerShowLoginMessage(): void {
    if (EventListenerController.listenerShowLoginMessage) {
      removeEventListener('listenerShowLoginMessage', EventListenerController.listenerShowLoginMessage);
      EventListenerController.listenerShowLoginMessage = undefined;
    }
  }
}
export default LoginController;
